import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { MultiValue, SingleValue } from "react-select";
import { AppDispatch } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import {
  dateFormatter,
  isSelectSingleValue,
  months,
} from "../../../core/utils";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  getUserByIdState,
  userByIdStateAction,
  UserRole,
} from "../../../redux/features/user-slice";
import WeekPicker, { Week } from "../../../components/week-picker";
import {
  getTeamMemberMonthlyReportsStateAction,
  getTeamMemberWeeklyReportsState,
  getTeamMemberWeeklyReportsStateAction,
  getUserDailyReportsState,
} from "../../../redux/features/report-slice";
import WeeklyReportTab from "../components/weekly-report-tab";
import MonthlyReportTab from "../components/monthly-report-tab";
import DailyReportTab from "../components/daily-report-tab";
import { exportToExcel } from "../../../core/utils/excel-helpers";
import SwitchTab from "../../../components/switch-tab";
import WeeklySummaryTab from "../components/weekly-summary-tab";
import { adminCat, mgTeam } from "../../../core/utils/constants";
import { AbsentDate } from "../../../redux/features/misa-slice";
import {
  clearWeeklySummaryActionsState,
  getWeeklySummaryActionsState,
} from "../../../redux/features/weekly-summary-slice";

const tabs = ["Daily Reports", "Weekly Reports", "Monthly Reports"];
const tabTypes = ["Reports", "1:1 Weekly Reports"];

export default function TeamMemberPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id, teamId } = useParams();

  const tabParam = searchParams.get("tab") ?? tabs[0];
  const tabTypeParam = searchParams.get("tabType") ?? tabTypes[1];

  const dispatch = useDispatch<AppDispatch>();
  const member = useSelector(getUserByIdState).data;
  const userDailyReports = useSelector(getUserDailyReportsState).data;
  const teamMemberReports = useSelector(getTeamMemberWeeklyReportsState).data;
  const weeklySummaryActionsState = useSelector(getWeeklySummaryActionsState);
  const errorState = useSelector(getErrorState);

  const [tab, setTab] = useState<string>(tabParam);
  const [tabType, setTabType] = useState<string>(tabTypeParam);
  const [weekRange, setWeekRange] = useState<Week>();
  const [selectedMonths, setSelectMonths] = useState<any[]>([]);
  const [absentDates, setAbsentDates] = useState<AbsentDate[]>([]);

  const fetchMemberById = useCallback(async () => {
    await dispatch(userByIdStateAction({ id: id as string }));
  }, [dispatch, id]);

  const fetchTeamMemberWeeklyReports = useCallback(async () => {
    if (weekRange && member) {
      await dispatch(
        getTeamMemberWeeklyReportsStateAction({
          userId: member.id,
          start: dateFormatter(weekRange.firstDay, "yyyy-MM-dd"),
          end: dateFormatter(weekRange.lastDay, "yyyy-MM-dd"),
        })
      );
    }
  }, [dispatch, weekRange, member]);

  const fetchTeamMemberMonthlyReports = useCallback(async () => {
    if (member) {
      await dispatch(
        getTeamMemberMonthlyReportsStateAction({
          userId: member.id,
          months: selectedMonths.map((e) => e.value),
        })
      );
    }
  }, [dispatch, member, selectedMonths]);

  useEffect(() => {
    fetchMemberById();
  }, [fetchMemberById]);

  useEffect(() => {
    if (weekRange) {
      fetchTeamMemberWeeklyReports();
    }
  }, [weekRange, fetchTeamMemberWeeklyReports]);

  useEffect(() => {
    fetchTeamMemberMonthlyReports();
  }, [selectedMonths, fetchTeamMemberMonthlyReports]);

  useEffect(() => {
    if (member) {
      dispatch(
        setBreadcrumbState([
          // { id: "member", path: null, title: "Categories" },
          // {
          //   id: `member-${member.team.id}`,
          //   path: -1,
          //   title: member.category.title,
          // },
          { id: `member-${member.id}`, path: null, title: member.name },
        ])
      );
    }
  }, [member]);

  useEffect(() => {
    if (weeklySummaryActionsState?.data?.success) {
      toast(weeklySummaryActionsState.data.message, { type: "success" });
      setTimeout(() => {
        dispatch(clearWeeklySummaryActionsState());
      }, 2000);
    }
  }, [weeklySummaryActionsState]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tab);
    setSearchParams(newParams, { replace: true });
  }, [tab, searchParams, setSearchParams]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tabType", tabType);
    setSearchParams(newParams, { replace: true });
  }, [tabType, searchParams, setSearchParams]);

  return (
    <PermissionGate categories={[]} roles={[]}>
      {member && (
        <>
          <Helmet title={member.name} />
          <div className="flex items-center pb-4 justify-between">
            <SwitchTab options={tabTypes} tab={tabType} setTab={setTabType} />
            <div className="flex items-center space-x-2">
              {tabType === tabTypes[0] && (
                <div className="max-w-1/2 flex items-center space-x-1 bg-action-selected p-0.5 rounded-lg">
                  {tabs.map((t: string) => (
                    <div
                      key={t}
                      className={`text-sm cursor-pointer p-1.5 truncate ${
                        t === tab
                          ? "shadow bg-background-paper rounded text-text-primary font-medium"
                          : "text-text-title"
                      }`}
                      onClick={() => setTab(t)}
                    >
                      {t}
                    </div>
                  ))}
                </div>
              )}
              {tab === tabs[0] || tab === tabs[1] || tabType === tabTypes[1] ? (
                <WeekPicker onChange={(week) => setWeekRange(week)} />
              ) : (
                <StyledSelect
                  options={months.map((month: any) => ({
                    value: month.value,
                    label: month.label,
                  }))}
                  value={{
                    value: "",
                    label:
                      selectedMonths.length > 0
                        ? selectedMonths.map((e) => e.label).join(", ")
                        : "All months",
                  }}
                  isClearable={false}
                  onChange={(
                    value: SingleValue<OptionValue> | MultiValue<OptionValue>
                  ) => {
                    if (value && isSelectSingleValue(value)) {
                      if (
                        selectedMonths.findIndex(
                          (e) => e.value === value.value
                        ) !== -1
                      ) {
                        setSelectMonths(
                          selectedMonths.filter(
                            (month) => month.value !== value.value
                          )
                        );
                      } else {
                        setSelectMonths([...selectedMonths, value]);
                      }
                    }
                  }}
                  exClassName="w-72"
                  exOption={(value: OptionValue) => {
                    return {
                      background:
                        selectedMonths.findIndex(
                          (e) => e.value === value.value
                        ) !== -1
                          ? "#f1f5f9"
                          : undefined,
                    };
                  }}
                />
              )}
              {/* <div
                className={`flex items-center rounded-lg p-2 cursor-pointer space-x-2 border border-divider hover:bg-action-hover ${
                  userDailyReports &&
                  Object.entries(userDailyReports).length > 0 &&
                  member &&
                  weekRange
                    ? "text-text-primary hover:bg-gray-200 cursor-pointer"
                    : "text-action-disabled"
                } `}
                onClick={() => {
                  if (
                    userDailyReports &&
                    Object.entries(userDailyReports).length > 0 &&
                    teamMemberReports &&
                    member &&
                    weekRange
                  ) {
                    // exportToExcel(
                    //   userDailyReports,
                    //   teamMemberReports.taskGroups,
                    //   absentDates,
                    //   `${member.name}.${
                    //     member.team.name
                    //   } Reports ${dateFormatter(
                    //     weekRange.firstDay,
                    //     "dd-MM-yyyy"
                    //   )} to ${dateFormatter(weekRange.lastDay, "dd-MM-yyyy")}`
                    // );
                  }
                }}
              >
                <img src="/excel-icon.svg" className="h-4 w-4" />
                <label className="text-sm cursor-pointer">Export</label>
              </div> */}
            </div>
          </div>
          {tabType === tabTypes[0] ? (
            tab === tabs[0] ? (
              <DailyReportTab
                user={member}
                weekRange={weekRange!}
                absentDates={absentDates}
                setAbsentDates={setAbsentDates}
              />
            ) : tab === tabs[1] ? (
              <WeeklyReportTab />
            ) : (
              <MonthlyReportTab />
            )
          ) : weekRange ? (
            <WeeklySummaryTab member={member} weekRange={weekRange} />
          ) : (
            <div />
          )}
        </>
      )}
      <ToastComponent id="member" />
    </PermissionGate>
  );
}
